import React from 'react';
import { action, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Switch from '@material-ui/core/Switch';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  ComposedChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Label,
} from 'recharts';

import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import { formatUnit, unitFormatter } from '../../utils';
import FilterListView from '../../components/FilterListView';
import { withRouter } from 'react-router-dom';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DataFrameDownload from '../../components/utils/DataFrameDownload';
import Cycle, { cycle } from '../../store/Cycle';
import { athenaRed } from '../../globals';

const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
    padding: theme.spacing(2),
    '& .recharts-legend-item': {
      cursor: 'pointer',
    },
  },
  paper: {
    maxHeight: '100%',
    overflow: 'auto',
  },
  boldColumn: {
    fontWeight: theme.typography.fontWeightBold,
  },
  emptyTechnology: {
    minHeight: 400,
    color: theme.palette.grey[600],
    textAlign: 'center',
  },
});

@withStyles(styles)
@withRouter
@observer
class TechnologyView extends BaseScenarioDashboardView {
  static apiSubPath = '/technology_overview/';

  @observable technology = null;
  @observable dateLevel = null;
  @observable scenario = null;
  @observable showCapacity = null;
  @observable unit = null;

  _setData(data) {
    this.technology = data.technology;
    this.dateLevel = data.date_level;
    this.showCapacity = this.showCapacity === null? true : this.showCapacity;
    this.scenario = data.scenario;
    this.unit = data.unit;
  }

  _getQueryParams() {
    return {
      scenario: this.scenario,
      technology: this.technology,
      date_level: this.dateLevel,
      unit: this.unit,
    };
  }

  onTechnologyChange = this.genericChangeHandler('technology');

  @action onDateLevelChange = event => {
    this.dateLevel = event.target.checked ? 'month' : 'quarter';
    this.updateData();
  };

  @action toggleCapacity = event => this.showCapacity = event.target.checked;
  
  onScenarioChange = this.genericChangeHandler('scenario');
  onUnitChange = this.genericChangeHandler('unit');

  get verboseUnit() {
    if (this.unit === 'TO') {
      return 't';
    }
    if (this.unit === 'KPC') {
      return 'k';
    }
    return '';
  }

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={650} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario}
            choices={this.data.scenario_choices}
            onScenarioChange={this.onScenarioChange}
          />
        </Grid>
        <Grid item>
          <Typography noWrap>
            Quarterly
            <Switch
              checked={this.dateLevel === 'month'}
              onChange={this.onDateLevelChange}
            />
            Monthly
          </Typography>
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Technology', this.data.technology_choices, this.technology,
            this.onTechnologyChange,
          )}
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Unit', this.data.unit_choices, this.unit, this.onUnitChange,
          )}
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/technology_overview/?' + this.getQueryString())}
            permission="athena.download_technology_dashboard"
          />
        </Grid>
      </Grid>
    );
  }

  formatGraphData(data){
    if(!this.showCapacity){ 
      data.forEach(record => {
        delete record['Available Capacity']
        delete record['Active Capacity']
        delete record['Max Capacity']
        delete record['Min Capacity']
      });
    }
    return data
  }

  get chart(){
    const formatter = unitFormatter(this.verboseUnit, 0);
    const scenario = Scenario.getForId(this.scenario);
    const scenarioCycle = scenario.cycle === null ? cycle : scenario.cycle;
    const actualsEndCycle = Cycle.fromOther(scenarioCycle, -1);
    const apoEndCycle = Cycle.fromOther(scenarioCycle, scenario.optimization_month_start);
    const data = this.formatGraphData(toJS(this.data.graph_data))
    return (
      <ResponsiveContainer aspect={21 / 9}>
        <ComposedChart data={[...data]}
            margin={{top: 20, right: 20, bottom: 20, left: 20}}>
          <CartesianGrid stroke='#f5f5f5'/>
          <XAxis dataKey="Date" stroke="#cdcdcd" />
          <YAxis allowDataOverflow={true} orientation="left" tickFormatter={formatter} stroke="#cdcdcd"/>
          <RechartsTooltip formatter={formatter} />
          <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
          {this.showCapacity && this.getArea('Max Capacity', { fill: '#cdcaf3', stroke: '#6861df' })}
          {this.showCapacity && this.getArea('Available Capacity', { fill: '#aeb9ff' })}
          {this.showCapacity && this.getArea('Active Capacity', { fill: '#dfdffc' })}
          {this.showCapacity && this.getArea('Min Capacity', { fill: '#f2f1fe', stroke: '#6861df' })}
          {this.getBar('Real Unserved Demand', { barSize: 40, fill: '#8a000b' })}
          {this.getBar('Allocated Demand', { barSize: 40, fill: '#f9ca24' })}
          {this.getBar('Production', { barSize: 40, fill: '#f5a623' })}
          {this.getLine('Plan to Produce in APO', { stroke: '#333', strokeWidth: 3 })}
          {this.getLine('Month Start Stock', { stroke: '#76b04e', strokeWidth: 4 })}
          {this.getLine('Month End Stock', { stroke: '#fc5c65', strokeWidth: 4 })}
          {this.getLine('Min Stock', { stroke: '#00a8d7', strokeDasharray: '3 6' })}
          {this.getLine('Target Stock', { stroke: '#0069b3', strokeWidth: 2, strokeDasharray: '6 3' })}
          {this.getLine('Max Stock', { stroke: '#008ad7', strokeDasharray: '3 6' })}
          <ReferenceLine
            x={actualsEndCycle.toString()}
            position="end"
            label={<Label position="top" fill={athenaRed}>Actuals end</Label>}
            strokeDasharray="3 3"
            stroke={athenaRed}
            isFront
          />
          <ReferenceLine
            x={apoEndCycle.toString()}
            position="start"
            label={<Label position="top" fill={athenaRed}>APO forecast end</Label>}
            strokeDasharray="3 3"
            stroke={athenaRed}
            isFront
          />
         </ComposedChart>
      </ResponsiveContainer>
    );
  }

  table(data, compare=false) {
    const { classes } = this.props;
    if (data.length<1){
      return <h1>There is no data to show.</h1>
    }
    const displayedColumns = Object.keys(data[0]);
    return (
      <Paper className={classes.paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {displayedColumns.map(key => (
                <TableCell key={key} align={key === 'index' ? 'left' : 'right'}>
                  {key === 'index' ? null : key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {this.data.table_data.map((row, index) => {
              const isRatio = row['index'] === 'Operational Efficiency';
              return (
                <TableRow key={index}>
                  {displayedColumns.map(key => (
                    <TableCell
                      key={key}
                      variant={key === 'index' ? 'head' : null}
                      align={key === 'index' ? 'left' : 'right'}
                      className={key.indexOf('FY') ? '' : classes.boldColumn}
                    >
                      {
                        key === 'index'
                          ? row[key]
                          : formatUnit(
                          row[key], isRatio ? '%' : this.verboseUnit, 0, isRatio,
                          )
                      }
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderContent() {
    if (!this.ready) {
      return <Grid item><Skeleton variant="rect" height={550} /></Grid>;
    }
    const { classes } = this.props;
    if (this.technology === null) {
      return (
        <Grid item container direction="column" wrap="nowrap" justify="center" className={classes.emptyTechnology}>
          <Grid item>Please select a technology</Grid>
        </Grid>
      );
    }
    return (
      <>
        <Grid item>
          <Paper className={classes.chartContainer}>
            <Grid container justify='space-between'>
              <Grid item>
                <Typography variant="h6">
                  Technology x Factories
                </Typography>
              </Grid>
              <Grid item>
                <Typography noWrap>
                Show Capacity
                  <Switch
                    checked={this.showCapacity}
                    onChange={this.toggleCapacity}
                  />
               </Typography>
              </Grid>
            </Grid>
            {this.chart}
          </Paper>
        </Grid>
        <Grid item>
          {this.table(this.data.table_data)}
        </Grid>
        {/*
        <Grid item>
          {this.table(this.data.comparison_table_data, true)}
        </Grid>
        */}
      </>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Technology Dashboard" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        {this.renderContent()}
      </Grid>
    );
  }

}

export default TechnologyView;
